import { isDate } from './validate'
import { EMarket } from '~/constants/enum'
export interface CookieSerializeOptions {
  domain?: string
  encode?(value: string): string
  expires?: Date
  httpOnly?: boolean
  maxAge?: number
  path?: string
  priority?: 'Low' | 'Medium' | 'High'
  sameSite?: 'lax' | 'strict' | 'none'
  secure?: boolean
}

export interface CookieParseOptions {
  decode?(value: string): string
}

const fieldContentRegExp = /^[\x20-\x7e\x80-\xff]+$/

export function parseCookie(
  str: string,
  options: CookieParseOptions = {}
): Record<string, string> {
  if (typeof str !== 'string') {
    throw new TypeError('argument str must be a string')
  }

  const obj: Record<string, string> = {}
  const dec = options.decode || decode

  let index = 0
  while (index < str.length) {
    const eqIdx = str.indexOf('=', index)
    if (eqIdx === -1) break

    let endIdx = str.indexOf(';', index)
    if (endIdx === -1) {
      endIdx = str.length
    } else if (endIdx < eqIdx) {
      index = str.lastIndexOf(';', eqIdx - 1) + 1
      continue
    }

    const key = str.slice(index, eqIdx).trim()
    if (obj[key] === undefined) {
      let val = str.slice(eqIdx + 1, endIdx).trim()
      if (val.charCodeAt(0) === 0x22) {
        val = val.slice(1, -1)
      }
      obj[key] = tryDecode(val, dec)
    }
    index = endIdx + 1
  }

  return obj
}

export function serializeCookie(
  name: string,
  value: string,
  options: CookieSerializeOptions = {}
): string {
  const enc = options.encode || encode

  if (typeof enc !== 'function') {
    throw new TypeError('option encode is invalid')
  }

  if (!fieldContentRegExp.test(name)) {
    throw new TypeError('argument name is invalid')
  }

  const encValue = enc(value)

  if (value && !fieldContentRegExp.test(value)) {
    throw new TypeError('argument val is invalid')
  }

  const parts = [`${name}=${encValue}`]

  if (options.maxAge != null) {
    const maxAge = options.maxAge - 0
    if (Number.isNaN(maxAge) || !Number.isFinite(maxAge)) {
      throw new TypeError('option maxAge is invalid')
    }
    parts.push(`Max-Age=${Math.floor(maxAge)}`)
  }

  if (options.domain && fieldContentRegExp.test(options.domain)) {
    parts.push(`Domain=${options.domain}`)
  }

  if (options.path && fieldContentRegExp.test(options.path)) {
    parts.push(`Path=${options.path}`)
  }

  if (options.expires) {
    if (!isDate(options.expires) || Number.isNaN(options.expires.valueOf())) {
      throw new TypeError('option expires is invalid')
    }
    parts.push(`Expires=${options.expires.toUTCString()}`)
  }

  if (options.httpOnly) {
    parts.push('HttpOnly')
  }

  if (options.secure) {
    parts.push('Secure')
  }

  if (options.priority) {
    const priority = String(options.priority).toLowerCase()
    switch (priority) {
      case 'low':
        parts.push('Priority=Low')
        break
      case 'medium':
        parts.push('Priority=Medium')
        break
      case 'high':
        parts.push('Priority=High')
        break
      default:
        throw new TypeError('option priority is invalid')
    }
  }

  if (options.sameSite) {
    const sameSite = options.sameSite.toLowerCase()

    switch (sameSite) {
      case 'strict':
        parts.push('SameSite=Strict')
        break
      case 'lax':
        parts.push('SameSite=Lax')
        break
      case 'none':
        parts.push('SameSite=None')
        break
      default:
        throw new TypeError('option sameSite is invalid')
    }
  }

  return parts.join('; ')
}

function decode(str: string): string {
  return str.includes('%') ? decodeURIComponent(str) : str
}

function encode(val: string): string {
  return encodeURIComponent(val)
}

function tryDecode(str: string, decode: (v: string) => string): string {
  try {
    return decode(str)
  } catch (e) {
    console.error('Cookie decode error:', e)
    return str
  }
}

export const listenCookieChange = (
  callback: (params: {
    oldCookie: Record<string, string>
    newCookie: Record<string, string>
  }) => void,
  interval = 500
): (() => void) => {
  let lastCookie = document.cookie
  const timer = setInterval(() => {
    const { cookie } = document
    if (cookie !== lastCookie) {
      try {
        callback({
          oldCookie: parseCookie(lastCookie),
          newCookie: parseCookie(cookie),
        })
      } finally {
        lastCookie = cookie
      }
    }
  }, interval)

  return () => clearInterval(timer)
}

export class CookieService {
  public domain: string
  public tokenName: string
  public market: EMarket = EMarket.VietNam

  public constructor(cookieDomain: string) {
    this.domain = cookieDomain
    this.tokenName = 'token'
  }

  public get(name: string, options?: CookieParseOptions): string | undefined {
    if (typeof window === 'undefined') return undefined

    try {
      return parseCookie(document.cookie, options)[name]
    } catch (error) {
      console.error('Cookie get error:', error)
      return undefined
    }
  }

  public set(
    key: string,
    value: string,
    options?: CookieSerializeOptions
  ): void {
    const defaultOptions: CookieSerializeOptions = {
      secure: true,
      path: '/',
      domain: this.domain,
      priority: 'Medium',
      httpOnly: false,
      sameSite: 'none',
      ...options,
    }
    try {
      document.cookie = serializeCookie(key, value, defaultOptions)
    } catch (error) {
      console.error('Cookie set error:', error)
    }
  }

  public setToken(token: string): void {
    const options: CookieSerializeOptions = {
      path: '/',
      secure: true,
      domain: this.domain,
      sameSite: 'none',
    }
    this.set(this.tokenName, token, options)
  }

  public getToken(): string | undefined {
    return this.get(this.tokenName)
  }

  public getMarket(): EMarket {
    return this.get('market') as EMarket
  }

  public clearToken(): void {
    const options: CookieSerializeOptions = {
      path: '/',
      secure: true,
      domain: this.domain,
      sameSite: 'none',
      expires: new Date(0),
    }
    this.set(this.tokenName, '', options)
  }
}
